import React, { useCallback, useMemo } from 'react';

import { getSeverity } from 'pages/item/v2/components/ProtectedData';
import { HistoryOfChangesItem } from 'pages/item/v2/types/history-of-changes';
import get from 'utils/get';
import isEmpty from 'utils/is-empty';
import isNil from 'utils/is-nil';

import ScorePane from '../ScorePane';
import TrendTooltip from '../TrendTooltip';

interface Props {
    data: {
        version: string;
        baseScore: number;
        vectorString: string;
        [key: string]: string | number | boolean;
    };
}

const Cvss: React.FC<Props> = ({ data }) => {
    const { version, baseScore, vectorString, ...vectorItems } = data || {};
    const [majorVersion] = useMemo(() => version?.split('.'), [version]);

    const field = 'cvss' + majorVersion;
    const label = field.toUpperCase();

    const getChart = useCallback(
        (historyData: HistoryOfChangesItem[]) => {
            if (historyData.every(({ changes }) => isNil(get(changes, ['metrics', field])))) {
                return null;
            } else {
                return historyData.reduce((output, { version, published, changes }) => {
                    const score = get<number>(changes, ['metrics', field, 'baseScore']) ?? output.at(-1)?.score ?? 0;
                    output.push({ version, score, published });
                    return output;
                }, [] as { score: number; version: number; published: Date }[]);
            }
        },
        [field]
    );

    if (isEmpty(data, { recursive: true })) {
        return null;
    }

    return (
        <TrendTooltip getChart={getChart} title={`${label} score statistics`}>
            <ScorePane
                score={baseScore}
                severity={getSeverity(baseScore)}
                name={label}
                vector={vectorString}
                vectorItems={vectorItems}
            />
        </TrendTooltip>
    );
};

export default Cvss;
